import { useState } from 'react';
import { Button } from '../../components/Button';
import type { SessionUser } from '../../net/api';
import { useInteractionStore } from '../../stores/interactionStore';
import { useMediaStore } from '../../stores/mediaStore';
import { usePlayersStore } from '../../stores/playersStore';
import { useViewStore } from '../../stores/viewStore';
import { is3dAvailable } from '../../renderer/createView';
import { istTouchGeraet } from '../../lib/eingabeart';
import { localMedia } from '../../webrtc/localMedia';
import { spatialAudio } from '../../webrtc/spatialAudio';
import { AvatarEditorModal } from '../profile/AvatarEditorModal';
import { useOffice } from '../office/OfficeContext';
import { useChatStore } from '../../stores/chatStore';
import { useChatUnread } from '../chat/useChatUnread';
import { HudLinks } from './HudLinks';
import { SettingsPopover } from './SettingsPopover';
import './hud.css';

/** Untere Steuerleiste: Medien-Toggles, Zonenanzeige, Einstellungen, Links. */
export function HudBar({ user }: { user: SessionUser }) {
  const { micArmed, camArmed, micLive, camLive, screenOn, mediaError, peers, setLocal } = useMediaStore();
  const zone = useInteractionStore((s) => s.currentZone);
  const selfEntry = usePlayersStore((s) => s.roster.find((e) => e.id === s.selfId));
  const { runtime, umgebung, istAdmin } = useOffice();
  const { mode: viewMode, ansichten, switching, setMode, setSwitching } = useViewStore();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const chatOpen = useChatStore((s) => s.isOpen);
  const setChatOpen = useChatStore((s) => s.setOpen);
  const chatUnread = useChatUnread();
  const [avatarOpen, setAvatarOpen] = useState(false);

  const inCall = Object.values(peers).some((p) => p.mediaActive);
  const canSwitchView = is3dAvailable() && ansichten === 'beide';

  /** Ansicht umschalten: die Runtime tauscht nur Renderer und Steuerung. */
  const toggleView = async (): Promise<void> => {
    if (switching) return;
    const next = viewMode === '3d' ? '2d' : '3d';
    setSwitching(true);
    try {
      await runtime.setViewMode(next);
      setMode(next);
    } finally {
      setSwitching(false);
    }
  };

  const toggleArmed = async (kind: 'mic' | 'cam', isArmed: boolean): Promise<void> => {
    spatialAudio.resume();
    setLocal({ mediaError: null });
    try {
      await localMedia.setArmed(kind, !isArmed);
    } catch {
      setLocal({ mediaError: 'Zugriff auf Kamera/Mikrofon verweigert' });
    }
  };

  const toggleScreen = async (): Promise<void> => {
    setLocal({ mediaError: null });
    try {
      if (screenOn) localMedia.stopScreen();
      else await localMedia.startScreen();
    } catch {
      setLocal({ mediaError: 'Bildschirmfreigabe abgebrochen' });
    }
  };

  /** aus | bereit (wartet auf Gespraech) | live */
  const mediaButton = (
    kind: 'mic' | 'cam',
    armed: boolean,
    live: boolean,
    icons: [string, string],
    labels: [string, string, string],
  ) => (
    <Button
      variant="toggle"
      active={armed}
      className={armed && !live ? 'hud__toggle--armed' : live ? 'hud__toggle--live' : ''}
      onClick={() => void toggleArmed(kind, armed)}
      title={live ? labels[2] : armed ? labels[1] : labels[0]}
      aria-pressed={armed}
    >
      {armed ? icons[0] : icons[1]}
      {live && <span className="hud__live-dot" aria-hidden="true" />}
    </Button>
  );

  return (
    <footer className="hud">
      <div className="hud__label">
        <span className="hud__umgebung">{umgebung.name}</span>
        {zone && <span className="hud__zone">{zone.name}</span>}
      </div>
      <div className="hud__controls">
        {mediaButton('mic', micArmed, micLive, ['🎙️', '🔇'], [
          'Mikrofon bereitschalten',
          'Mikrofon bereit — startet im Gespräch',
          'Mikrofon läuft — ausschalten',
        ])}
        {mediaButton('cam', camArmed, camLive, ['📷', '🚫'], [
          'Kamera bereitschalten',
          'Kamera bereit — startet im Gespräch',
          'Kamera läuft — ausschalten',
        ])}
        <Button
          variant="toggle"
          active={screenOn}
          onClick={() => void toggleScreen()}
          disabled={!inCall && !screenOn}
          title={screenOn ? 'Teilen beenden' : inCall ? 'Bildschirm teilen' : 'Bildschirm teilen — nur im Gespräch'}
          aria-pressed={screenOn}
        >
          🖥️
        </Button>
        {canSwitchView && (
          <Button
            variant="toggle"
            active={viewMode === '3d'}
            onClick={() => void toggleView()}
            disabled={switching}
            title={
              viewMode === '3d'
                ? 'Zurück zur Draufsicht'
                : istTouchGeraet()
                  ? 'Ego-Perspektive — links laufen, rechts umsehen'
                  : 'Ego-Perspektive — Maus dreht, WASD läuft'
            }
            aria-pressed={viewMode === '3d'}
          >
            {viewMode === '3d' ? '🗺️' : '🕶️'}
          </Button>
        )}
        {/* Auf dem Handy ersetzt dieser Knopf den Chat-Knopf links unten — dort liegt der Joystick */}
        <Button
          variant="toggle"
          className="hud__chat"
          active={chatOpen}
          onClick={() => setChatOpen(!chatOpen)}
          title="Chat"
          aria-pressed={chatOpen}
        >
          💬
          {chatUnread > 0 && <span className="hud__badge">{chatUnread}</span>}
        </Button>
        <div className="hud__settings-anchor">
          <Button
            variant="toggle"
            active={settingsOpen}
            onClick={() => setSettingsOpen((open) => !open)}
            title="Einstellungen"
            aria-expanded={settingsOpen}
          >
            ⚙️
          </Button>
          {settingsOpen && (
            <SettingsPopover
              onClose={() => setSettingsOpen(false)}
              links={<HudLinks user={user} onAvatar={() => setAvatarOpen(true)} />}
            />
          )}
        </div>
      </div>
      <div className="hud__meta">
        <HudLinks user={user} onAvatar={() => setAvatarOpen(true)} />
      </div>
      {mediaError && <p className="hud__error" role="alert">{mediaError}</p>}
      {avatarOpen && (
        <AvatarEditorModal
          onClose={() => setAvatarOpen(false)}
          onSaved={(avatar) => runtime.setSelfAvatar(avatar)}
          fallbackColor={selfEntry?.avatarColor ?? 0}
        />
      )}
    </footer>
  );
}
